import React, { useMemo } from "react";
import { operationalRelationships, nodeColors, type OperationalNode } from "../data/operationsNexus";

interface OperationsGraphProps {
  nodes: OperationalNode[];
  selectedId: string;
  onSelect: (id: string) => void;
}

export const OperationsGraph: React.FC<OperationsGraphProps> = ({ nodes, selectedId, onSelect }) => {
  const nodeMap = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const visibleIds = useMemo(() => new Set(nodes.map((node) => node.id)), [nodes]);
  const connectedIds = useMemo(() => {
    const ids = new Set<string>([selectedId]);
    operationalRelationships.forEach((relationship) => {
      if (relationship.source === selectedId) ids.add(relationship.target);
      if (relationship.target === selectedId) ids.add(relationship.source);
    });
    return ids;
  }, [selectedId]);

  const visibleRelationships = operationalRelationships.filter(
    (relationship) => visibleIds.has(relationship.source) && visibleIds.has(relationship.target),
  );

  return (
    <div className="operations-graph" role="application" aria-label="Interactive operations relationship graph">
      <svg viewBox="0 0 1000 560" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="node-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <linearGradient id="edge-gradient" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#536476" stopOpacity=".25" /><stop offset=".5" stopColor="#91a9bd" stopOpacity=".7" /><stop offset="1" stopColor="#536476" stopOpacity=".2" /></linearGradient>
          <pattern id="graph-grid" width="32" height="32" patternUnits="userSpaceOnUse"><path d="M 32 0 L 0 0 0 32" fill="none" stroke="#91a9bd" strokeOpacity=".045" strokeWidth="1" /></pattern>
        </defs>
        <rect width="1000" height="560" fill="url(#graph-grid)" />

        <g className="graph-edges">
          {visibleRelationships.map((relationship) => {
            const source = nodeMap.get(relationship.source);
            const target = nodeMap.get(relationship.target);
            if (!source || !target) return null;
            const focused = relationship.source === selectedId || relationship.target === selectedId;
            const midX = (source.x + target.x) / 2;
            const path = `M ${source.x} ${source.y} C ${midX} ${source.y}, ${midX} ${target.y}, ${target.x} ${target.y}`;
            return (
              <g key={relationship.id} className={focused ? "edge focused" : "edge"}>
                <path d={path} className="edge-hit" />
                <path d={path} className={relationship.evidenceStatus.toLowerCase()} />
                {focused && <text x={midX} y={(source.y + target.y) / 2 - 7} textAnchor="middle">{relationship.label.toUpperCase()}</text>}
              </g>
            );
          })}
        </g>

        <g className="graph-nodes">
          {nodes.map((node) => {
            const selected = selectedId === node.id;
            const connected = connectedIds.has(node.id);
            return (
              <g
                key={node.id}
                className={`operation-node ${selected ? "selected" : ""} ${connected ? "connected" : "dimmed"}`}
                transform={`translate(${node.x} ${node.y})`}
                role="button"
                tabIndex={0}
                aria-label={`${node.type}: ${node.name}`}
                onClick={() => onSelect(node.id)}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onSelect(node.id); }}
              >
                {selected && <circle r="31" fill="none" stroke={nodeColors[node.type]} strokeOpacity=".35" className="node-pulse" />}
                <circle r={selected ? 16 : 12} fill="#081015" stroke={nodeColors[node.type]} strokeWidth={selected ? 2.5 : 1.5} filter={selected ? "url(#node-glow)" : undefined} />
                <circle r={selected ? 5 : 3.5} fill={nodeColors[node.type]} />
                <text y={selected ? 31 : 27} textAnchor="middle" className="node-type">{node.type.toUpperCase()}</text>
                <text y={selected ? 45 : 40} textAnchor="middle" className="node-name">{node.name}</text>
                <text y={selected ? 58 : 53} textAnchor="middle" className={`node-status ${node.evidenceStatus.toLowerCase()}`}>{node.evidenceStatus}</text>
              </g>
            );
          })}
        </g>
      </svg>
      <div className="graph-axis graph-axis-left"><span>ENTITY</span><span>WORK</span><span>SIGNAL</span><span>ACTION</span></div>
      <div className="graph-path-label">CLIENT → PROCESS → WORK → SIGNAL → IMPROVEMENT → DECISION</div>
    </div>
  );
};
